export type FilterCallback = (filters: number[]) => void;

class FilterService {
  private static instance: FilterService;
  private subscribers: FilterCallback[] = [];
  private selectedBrands: number[] = [];

  private constructor() {}

  public static getInstance(): FilterService {
    if (!FilterService.instance) {
      FilterService.instance = new FilterService();
    }
    return FilterService.instance;
  }

  public getFilters(): number[] {
    return this.selectedBrands;
  }

  public setFilters(brands: number[]): void {
    this.selectedBrands = brands;
    this.notifySubscribers();
  }

  public addFilter(brandId: number): void {
    if (!this.selectedBrands.includes(brandId)) {
      this.selectedBrands = [...this.selectedBrands, brandId];
      this.notifySubscribers();
    }
  }

  public removeFilter(brandId: number): void {
    const newFilters = this.selectedBrands.filter(id => id !== brandId);
    if (newFilters.length !== this.selectedBrands.length) {
      this.selectedBrands = newFilters;
      this.notifySubscribers();
    }
  }

  public clearFilters(): void {
    if (this.selectedBrands.length > 0) {
      this.selectedBrands = [];
      this.notifySubscribers();
    }
  }

  public toggleFilter(brandId: number): void {
    if (this.selectedBrands.includes(brandId)) {
      this.removeFilter(brandId);
    } else {
      this.addFilter(brandId);
    }
  }

  /**
   * Subscribe to changes on the selected filters.
   * Notifies immediately with the current filters.
   * Accepts an optional AbortSignal to automatically unsubscribe when aborted.
   * @param callback A function that will be called whenever the filters change.
   * @param options Optional object containing an AbortSignal.
   * @returns An unsubscribe function.
   */
  public subscribe(callback: FilterCallback, options?: { signal?: AbortSignal }): () => void {
    this.subscribers.push(callback);
    callback(this.selectedBrands);

    let abortHandler: () => void;

    const unsubscribe = () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
      if (options?.signal && abortHandler) {
        options.signal.removeEventListener('abort', abortHandler);
      }
    };

    if (options?.signal) {
      if (options.signal.aborted) {
        unsubscribe();
      } else {
        abortHandler = () => unsubscribe();
        options.signal.addEventListener('abort', abortHandler);
      }
    }

    return unsubscribe;
  }

  /**
   * Calls all subscriber callbacks with the updated filters.
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback(this.selectedBrands));
  }
}

export const filterService = FilterService.getInstance();
